import { useEffect, useRef, useState } from 'react';
import { Box, Button, Spinner, Stack, Text } from '@chakra-ui/react';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import AuthHeader from '@/components/auth-header';
import { useAuth } from '@/lib/hooks/use-auth';
import api from '@/utils/api';

const errorMessages: Record<string, string> = {
  sso_failed: 'We could not complete single sign-on. Please try again.',
  sso_denied: 'Your identity provider denied the sign-in request.',
  sso_unavailable: 'Single sign-on is not available for this workspace.',
  email_not_allowed: 'Your email domain is not permitted for this workspace.',
  invalid_request: 'The sign-in response was invalid. Please try again.',
};

// SSOCallback is the landing page after the IdP redirect. It trades the
// one-time handoff code for a session, then drops the user into their default
// workspace — the same destination as a password login.
const SSOCallback = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const [error, setError] = useState<string | null>(null);
  // The handoff code is single-use; guard against React StrictMode's double
  // effect invocation so we don't consume it twice and fail the second time.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const idpError = params.get('error');
    if (idpError) {
      setError(errorMessages[idpError] || errorMessages.sso_failed);
      return;
    }
    const code = params.get('code');
    if (!code) {
      setError(errorMessages.invalid_request);
      return;
    }

    (async () => {
      try {
        const res = await api.post('auth/sso/exchange', { code });
        const user = auth.establishSession(res.data);
        navigate(`/${user.default_workspace_id}/home`, { replace: true });
      } catch {
        setError(errorMessages.sso_failed);
      }
    })();
  }, [params, navigate, auth]);

  if (error) {
    return (
      <Stack gap={6}>
        <AuthHeader title='Sign-in failed' />
        <Text textAlign='center' color='fg.muted'>
          {error}
        </Text>
        <Box textAlign='center'>
          <Button asChild>
            <NavLink to='/auth/login'>Back to login</NavLink>
          </Button>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack gap={6} align='center'>
      <AuthHeader title='Signing you in…' />
      <Spinner size='lg' />
      <Text color='fg.muted'>Completing single sign-on…</Text>
    </Stack>
  );
};

export default SSOCallback;
